import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Snackbar,
  Checkbox,
  Toolbar,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Check as CheckIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
  FileDownload as FileDownloadIcon,
  TableView as TableViewIcon,
  Archive as ArchiveIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { es } from 'date-fns/locale';
import { format } from 'date-fns';

// Componentes personalizados
import OrderStepper from '../components/orders/OrderStepper';
import OrderForm from '../components/orders/OrderForm';
import OrderViewer from '../components/orders/OrderViewer';
import FileDropZone from '../components/FileDropZone';

// Servicios
import { orderService } from '../services/orderService';
import { providerService } from '../services/providerService';

// Datos de empresas (ahora están dentro de OrderForm)
const COMPANIES = [
  'Conlima SLU', 
  'Conlima Contratas y Servicios SLU', 
  'Renting Grupo Hermes SLU'
];

// Mapeo de estados a etiquetas y colores
const statusConfig = {
  CREATED: { label: 'Creado', color: 'primary' },
  PENDING_SIGNATURE: { label: 'Pendiente de Firma', color: 'warning' },
  PENDING_TRANSFER: { label: 'Pendiente de Transferencia', color: 'info' },
  PENDING_PAYMENT: { label: 'Pendiente de Pago', color: 'warning' },
  COMPLETED: { label: 'Completado', color: 'success' },
  CANCELLED: { label: 'Cancelado', color: 'error' }
};

function Orders() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Estado para la tabla
  const [orders, setOrders] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedOrders, setSelectedOrders] = useState([]); // Para selección múltiple
  const [allOrderIds, setAllOrderIds] = useState([]); // Todos los IDs de pedidos filtrados
  const [selectAllPages, setSelectAllPages] = useState(false); // Si está seleccionando todas las páginas
  const [isExporting, setIsExporting] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    providerId: '',
    company: '',
    startDate: null,
    endDate: null,
    invoiceStartDate: null,
    invoiceEndDate: null
  });
  
  // Estado para los modales
  const [openOrderModal, setOpenOrderModal] = useState(false);
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [openConfirmCancelModal, setOpenConfirmCancelModal] = useState(false);
  const [openPreviewModal, setOpenPreviewModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [notificationEmail, setNotificationEmail] = useState('');
  
  // Estado para feedback
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Cargar datos al montar el componente
  useEffect(() => {
    loadOrders();
    loadProviders();
  }, [page, rowsPerPage, filters.status, filters.providerId, filters.company, filters.startDate, filters.endDate, filters.invoiceStartDate, filters.invoiceEndDate]);

  // Función para cargar pedidos
  const loadOrders = async () => {
    setLoading(true);
    // No limpiar selección al cargar nuevos datos
    try {
      const params = {
        page: page + 1, // Backend usa páginas 1-indexed
        limit: rowsPerPage,
        status: filters.status,
        providerId: filters.providerId,
        company: filters.company,
        startDate: filters.startDate ? format(filters.startDate, 'yyyy-MM-dd') : undefined,
        endDate: filters.endDate ? format(filters.endDate, 'yyyy-MM-dd') : undefined,
        invoiceStartDate: filters.invoiceStartDate ? format(filters.invoiceStartDate, 'yyyy-MM-dd') : undefined,
        invoiceEndDate: filters.invoiceEndDate ? format(filters.invoiceEndDate, 'yyyy-MM-dd') : undefined
      };

      const result = await orderService.getOrders(params);
      
      if (result.success) {
        // Normalizar todos los pedidos
        const normalizedOrders = (result.data.orders || []).map(normalizeOrderData);
        setOrders(normalizedOrders);
        setTotalCount(result.data.total || 0);

        // Obtener todos los IDs para el "seleccionar todo"
        if (page === 0) { // Solo en la primera página o cuando cambian los filtros
          const allParams = {
            ...params,
            page: 1,
            limit: result.data.total || 10000 // Obtener todos los resultados
          };
          
          try {
            const allResult = await orderService.getOrders(allParams);
            if (allResult.success) {
              const allIds = (allResult.data.orders || []).map(order => order.id);
              setAllOrderIds(allIds);
            }
          } catch (error) {
            console.warn('Error al cargar todos los IDs:', error);
          }
        }
      } else {
        showSnackbar('Error al cargar pedidos: ' + result.message, 'error');
      }
    } catch (error) {
      showSnackbar('Error al cargar pedidos', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Función para cargar proveedores
  const loadProviders = async () => {
    try {
      const result = await providerService.getProviders({ limit: 1000 }); // Obtener todos
      
      if (result.success) {
        setProviders(result.data.providers || []);
      }
    } catch (error) {
      console.error('Error al cargar proveedores:', error);
    }
  };

  // Función para mostrar mensajes
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Función helper para normalizar datos de pedido
  const normalizeOrderData = (order) => {
    // Helper para validar y convertir fechas
    const validateDate = (dateValue) => {
      if (!dateValue) return new Date();
      const date = new Date(dateValue);
      return isNaN(date.getTime()) ? new Date() : date;
    };

    return {
      ...order,
      amount: order.amount !== null && order.amount !== undefined ? Number(order.amount) : 0,
      creationDate: validateDate(order.creationDate || order.createdAt),
      lastUpdate: validateDate(order.lastUpdate || order.updatedAt),
      invoiceDate: order.invoiceDate ? validateDate(order.invoiceDate) : null
    };
  };

  // Manejadores de eventos
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0);
    // Limpiar selección cuando cambian los filtros
    setSelectedOrders([]);
    setSelectAllPages(false);
  };

  // Funciones para selección múltiple
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      // Seleccionar todas las órdenes de todas las páginas
      setSelectedOrders([...allOrderIds]);
      setSelectAllPages(true);
    } else {
      setSelectedOrders([]);
      setSelectAllPages(false);
    }
  };

  const handleSelectOrder = (orderId) => {
    const selectedIndex = selectedOrders.indexOf(orderId);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedOrders, orderId);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedOrders.slice(1));
    } else if (selectedIndex === selectedOrders.length - 1) {
      newSelected = newSelected.concat(selectedOrders.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedOrders.slice(0, selectedIndex),
        selectedOrders.slice(selectedIndex + 1),
      );
    }

    setSelectedOrders(newSelected);
  };

  const isOrderSelected = (orderId) => selectedOrders.indexOf(orderId) !== -1;

  const handleBulkApprove = async () => {
    if (selectedOrders.length === 0) {
      showSnackbar('Debe seleccionar al menos una orden', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      // Filtrar solo las órdenes que están en estado PENDING_SIGNATURE
      const approvableOrders = orders.filter(order => 
        selectedOrders.includes(order.id) && order.status === 'PENDING_SIGNATURE'
      );

      if (approvableOrders.length === 0) {
        showSnackbar('Ninguna de las órdenes seleccionadas puede ser aprobada (deben estar en estado "Pendiente de Firma")', 'warning');
        setIsSubmitting(false);
        return;
      }

      if (approvableOrders.length < selectedOrders.length) {
        showSnackbar(`Solo ${approvableOrders.length} de ${selectedOrders.length} órdenes seleccionadas pueden ser aprobadas`, 'info');
      }

      let successCount = 0;
      let errorCount = 0;

      for (const order of approvableOrders) {
        try {
          const result = await orderService.approveOrder(order.id);
          if (result.success) {
            successCount++;
          } else {
            errorCount++;
          }
        } catch (error) {
          errorCount++;
        }
      }

      if (successCount > 0) {
        showSnackbar(`${successCount} pedido(s) aprobado(s) correctamente${errorCount > 0 ? `, ${errorCount} con errores` : ''}`, 
          errorCount > 0 ? 'warning' : 'success');
        loadOrders();
        setSelectedOrders([]);
      } else {
        showSnackbar('Error al aprobar los pedidos', 'error');
      }
    } catch (error) {
      showSnackbar('Error al procesar la aprobación masiva', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBulkCancel = () => {
    if (selectedOrders.length === 0) {
      showSnackbar('Debe seleccionar al menos una orden', 'error');
      return;
    }

    // Filtrar las órdenes que se pueden cancelar (no canceladas ni completadas)
    const cancellableOrders = orders.filter(order => 
      selectedOrders.includes(order.id) && 
      order.status !== 'COMPLETED' && 
      order.status !== 'CANCELLED'
    );

    if (cancellableOrders.length === 0) {
      showSnackbar('Ninguna de las órdenes seleccionadas puede ser cancelada (ya están completadas o canceladas)', 'warning');
      return;
    }

    if (cancellableOrders.length < selectedOrders.length) {
      showSnackbar(`Solo ${cancellableOrders.length} de ${selectedOrders.length} órdenes seleccionadas pueden ser canceladas`, 'info');
    }

    // Abrir modal de cancelación masiva (usaremos el mismo modal pero adaptado)
    setSelectedOrder({ id: 'bulk', orders: cancellableOrders });
    setOpenCancelModal(true);
    setCancelReason('');
    setNotificationEmail('');
  };

  const handleOpenOrderModal = async (order = null) => {
    if (order) {
      try {
        // Cargar los datos completos del pedido incluyendo el historial y stepSummary
        const result = await orderService.getOrder(order.id);
        
        if (result.success) {
          setSelectedOrder(result.data);
        } else {
          setSelectedOrder(order); // Fallback al pedido original
          showSnackbar('Advertencia: No se pudieron cargar los detalles completos', 'warning');
        }
      } catch (error) {
        setSelectedOrder(order); // Fallback al pedido original
        showSnackbar('Advertencia: No se pudieron cargar los detalles completos', 'warning');
      }
    } else {
      setSelectedOrder(null);
    }
    setOpenOrderModal(true);
    setError(null);
  };

  const handleCloseOrderModal = () => {
    setOpenOrderModal(false);
    setSelectedOrder(null);
    setError(null);
  };



  const handleOrderCreated = (newOrder) => {
    // Normalizar los datos del pedido
    const normalizedOrder = normalizeOrderData(newOrder);
    
    // Añadir el nuevo pedido a la lista
    setOrders(prev => [normalizedOrder, ...prev]);
    setTotalCount(prev => prev + 1);
    
    // Cerrar el modal de creación y abrir el de edición
    setSelectedOrder(normalizedOrder);
    showSnackbar('Pedido creado correctamente');
  };

  const handleSubmitOrder = async (values) => {
    setIsSubmitting(true);
    setError(null);

    try {
      let result;
      
      if (!selectedOrder) {
        showSnackbar('Error: debe crear el pedido subiendo un archivo primero', 'error');
        return;
      }

      // Manejar cada estado específico
      switch (selectedOrder.status) {
        case 'CREATED':
          // Completar detalles y pasar a firma
          result = await orderService.updateOrderProvider(selectedOrder.id, values);
          break;
          
        case 'PENDING_SIGNATURE':
          // Aprobar pedido y pasar a transferencia o cancelar
          if (values.approved) {
            result = await orderService.approveOrder(selectedOrder.id);
          } else if (values.cancel) {
            // Abrir modal de cancelación
            setOpenCancelModal(true);
            setIsSubmitting(false);
            return;
          } else {
            // Abrir modal de cancelación por defecto
            setOpenCancelModal(true);
            setIsSubmitting(false);
            return;
          }
          break;
          
        case 'PENDING_TRANSFER':
          // Definir método de pago
          // Si es Contado o Recibo a la vista, también incluir datos de pago
          if (values.paymentMethod === 'Contado' || values.paymentMethod === 'Recibo a la vista') {
            // Validar que se haya proporcionado la fecha de pago
            if (!values.paymentDate) {
              showSnackbar('La fecha de pago es requerida para este método de pago', 'error');
              setIsSubmitting(false);
              return;
            }
            
            // Asegurar que la fecha esté en formato ISO
            let paymentDateFormatted = values.paymentDate;
            if (values.paymentDate instanceof Date) {
              paymentDateFormatted = values.paymentDate.toISOString();
            }
            
            const paymentData = {
              paymentMethod: values.paymentMethod,
              paymentDate: paymentDateFormatted,
              comments: values.comments || ''
            };
            
            result = await orderService.updatePaymentMethod(selectedOrder.id, paymentData);
          } else {
            // Para Transferencia, usar el flujo normal
            result = await orderService.updatePaymentMethod(selectedOrder.id, values.paymentMethod);
          }
          break;
          
        case 'PENDING_PAYMENT':
          // Marcar como pagado
          result = await orderService.markOrderAsPaid(selectedOrder.id, {
            paymentDate: values.paymentDate,
            comments: values.comments
          });
          break;
          
        default:
          showSnackbar('Estado de pedido no válido', 'error');
          return;
      }

      if (result.success) {
        const statusMessages = {
          'CREATED': 'Detalles guardados. Pedido listo para firma.',
          'PENDING_SIGNATURE': values.approved ? 'Pedido aprobado correctamente' : 'Pedido cancelado',
          'PENDING_TRANSFER': 
            (values.paymentMethod === 'Contado' || values.paymentMethod === 'Recibo a la vista') 
              ? 'Pedido completado con pago inmediato' 
              : 'Método de pago actualizado',
          'PENDING_PAYMENT': 'Pedido marcado como pagado y completado'
        };
        
        showSnackbar(statusMessages[selectedOrder.status] || 'Pedido actualizado correctamente');
        handleCloseOrderModal();
        loadOrders(); // Recargar la lista
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError(err.message || 'Error al procesar el pedido');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteOrder = async (order) => {
    try {
      // Cargar los datos completos del pedido para el modal de cancelación
      const result = await orderService.getOrder(order.id);
      
      if (result.success) {
        setSelectedOrder(result.data);
      } else {
        setSelectedOrder(order); // Fallback al pedido original
        console.warn('No se pudieron cargar los detalles completos del pedido');
      }
    } catch (error) {
      setSelectedOrder(order); // Fallback al pedido original
      console.warn('Error al cargar detalles del pedido:', error);
    }
    
    setOpenCancelModal(true);
    setCancelReason('');
    setNotificationEmail('');
  };

  const handleConfirmCancel = async () => {
    if (!cancelReason.trim()) {
      showSnackbar('Debe proporcionar un motivo de cancelación', 'error');
      return;
    }

    if (!notificationEmail.trim()) {
      showSnackbar('Debe proporcionar un email para la notificación', 'error');
      return;
    }

    // Validar formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(notificationEmail)) {
      showSnackbar('El formato del email no es válido', 'error');
      return;
    }

    // Abrir modal de confirmación final
    setOpenConfirmCancelModal(true);
  };

  const handleFinalConfirmCancel = async () => {
    setIsSubmitting(true);
    
    try {
      // Verificar si es cancelación masiva
      if (selectedOrder.id === 'bulk') {
        let successCount = 0;
        let errorCount = 0;

        for (const order of selectedOrder.orders) {
          try {
            const result = await orderService.cancelOrder(order.id, cancelReason);
            if (result.success) {
              successCount++;
              
              // Enviar email de notificación para cada orden
              try {
                const emailPayload = {
                  "canal": "EMAIL",
                  "tipo": "comunicacion",
                  "datos": {
                    "encriptado": 1,
                    "email": notificationEmail,
                    "asunto": "CONLIMA - Pedido Cancelado",
                    "texto": `El pedido ${order.id} ha sido cancelado. Motivo: ${cancelReason}`
                  }
                };

                await orderService.sendNotificationEmail(emailPayload);
              } catch (emailError) {
                console.error('Error al enviar email para pedido', order.id, ':', emailError);
              }
            } else {
              errorCount++;
            }
          } catch (error) {
            errorCount++;
          }
        }

        if (successCount > 0) {
          showSnackbar(`${successCount} pedido(s) cancelado(s) correctamente${errorCount > 0 ? `, ${errorCount} con errores` : ''}`, 
            errorCount > 0 ? 'warning' : 'success');
          setSelectedOrders([]); // Limpiar selección
        } else {
          showSnackbar('Error al cancelar los pedidos', 'error');
        }
      } else {
        // Cancelación individual
        const result = await orderService.cancelOrder(selectedOrder.id, cancelReason);
        
        if (result.success) {
          // Enviar email de notificación
          try {
            const emailPayload = {
              "canal": "EMAIL",
              "tipo": "comunicacion",
              "datos": {
                "encriptado": 1,
                "email": notificationEmail,
                "asunto": "CONLIMA - Pedido Cancelado",
                "texto": `El pedido ${selectedOrder.id} ha sido cancelado. Motivo: ${cancelReason}`
              }
            };

            await orderService.sendNotificationEmail(emailPayload);
            
            showSnackbar('Pedido cancelado correctamente y email enviado');
          } catch (emailError) {
            console.error('Error al enviar email:', emailError);
            showSnackbar('Pedido cancelado correctamente, pero hubo un error al enviar el email', 'warning');
          }
        } else {
          showSnackbar('Error al cancelar pedido: ' + result.message, 'error');
        }
      }

      // Cerrar todos los modales y limpiar estados
      setOpenCancelModal(false);
      setOpenConfirmCancelModal(false);
      setCancelReason('');
      setNotificationEmail('');
      setSelectedOrder(null);
      loadOrders(); // Recargar la lista
    } catch (err) {
      showSnackbar('Error al cancelar pedido(s)', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelDialogClose = () => {
    setOpenCancelModal(false);
    setOpenConfirmCancelModal(false);
    setCancelReason('');
    setNotificationEmail('');
    setSelectedOrder(null);
  };

  const handleOpenPreviewModal = async (order) => {
    try {
      // Cargar los datos completos del pedido incluyendo archivos
      const result = await orderService.getOrder(order.id);
      
      if (result.success && result.data.files && result.data.files.length > 0) {
        setSelectedOrder(result.data);
        setOpenPreviewModal(true);
      } else {
        showSnackbar('Este pedido no tiene archivos adjuntos', 'warning');
      }
    } catch (error) {
      showSnackbar('Error al cargar los archivos del pedido', 'error');
    }
  };

  const handleClosePreviewModal = () => {
    setOpenPreviewModal(false);
    setSelectedOrder(null);
  };

  const handleExportExcel = async () => {
    setIsExporting(true);
    try {
      const orderIds = selectedOrders;

      if (orderIds.length === 0) {
        showSnackbar('No hay pedidos seleccionados para exportar', 'warning');
        return;
      }

      // Llamar al endpoint de exportación
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/orders/export/excel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ orderIds })
      });

      if (!response.ok) {
        throw new Error('Error al exportar');
      }

      // Descargar el archivo
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `pedidos_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      window.URL.revokeObjectURL(url);
      
      showSnackbar(`Excel exportado correctamente (${orderIds.length} pedidos)`, 'success');
    } catch (error) {
      showSnackbar('Error al exportar Excel', 'error');
      console.error('Error exportando Excel:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleDownloadInvoices = async () => {
    setIsExporting(true);
    try {
      const orderIds = selectedOrders;

      if (orderIds.length === 0) {
        showSnackbar('No hay pedidos seleccionados para descargar facturas', 'warning');
        return;
      }

      // Llamar al endpoint de descarga de facturas
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/orders/download/invoices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ orderIds })
      });

      if (!response.ok) {
        throw new Error('Error al descargar facturas');
      }

      // Descargar el archivo ZIP
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `facturas_${format(new Date(), 'yyyyMMdd_HHmmss')}.zip`;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      window.URL.revokeObjectURL(url);
      
      showSnackbar(`ZIP con facturas descargado correctamente (${orderIds.length} pedidos)`, 'success');
    } catch (error) {
      showSnackbar('Error al descargar facturas', 'error');
      console.error('Error descargando facturas:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleFileUpload = async (file) => {
    try {
      if (selectedOrder) {
        // Añadir archivo a pedido existente
        const result = await orderService.addFileToOrder(selectedOrder.id, file);
        
        if (result.success) {
          showSnackbar('Archivo añadido correctamente');
          // Recargar información del pedido
          const orderResult = await orderService.getOrder(selectedOrder.id);
          if (orderResult.success) {
            setSelectedOrder(orderResult.data);
          }
        } else {
          showSnackbar('Error al añadir archivo: ' + result.message, 'error');
        }
      }
    } catch (error) {
      showSnackbar('Error al subir archivo', 'error');
    }
  };

  const handleFileDelete = async (file) => {
    if (window.confirm('¿Está seguro de que desea eliminar este archivo?')) {
      try {
        const result = await orderService.deleteFile(file.id);
        
        if (result.success) {
          showSnackbar('Archivo eliminado correctamente');
          // Recargar información del pedido
          const orderResult = await orderService.getOrder(selectedOrder.id);
          if (orderResult.success) {
            setSelectedOrder(orderResult.data);
          }
        } else {
          showSnackbar('Error al eliminar archivo: ' + result.message, 'error');
        }
      } catch (err) {
        showSnackbar('Error al eliminar archivo', 'error');
      }
    }
  };

  const handleFileDownload = async (file) => {
    try {
      // Construir URL de descarga del backend
      const downloadUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/orders/files/${file.id}/download`;
      
      // Obtener token del localStorage
      const token = localStorage.getItem('token');
      
      // Hacer petición con token
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Error al descargar el archivo');
      }
      
      // Obtener el blob del archivo
      const blob = await response.blob();
      
      // Crear enlace temporal para descargar
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.filename || file.name;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Limpiar URL temporal
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showSnackbar('Error al descargar el archivo', 'error');
    }
  };

  return (
    <Box sx={{ padding: 3 }}>
      {/* Título y botón nuevo pedido */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Gestión de Pedidos</Typography>
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={<AddIcon />}
          onClick={() => handleOpenOrderModal()}
          sx={{ fontWeight: 'bold' }}
        >
          Nuevo Pedido
        </Button>
      </Box>

      {/* Barra de filtros */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          Filtros de Búsqueda
        </Typography>
        
        {/* Filtros de categorías */}
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Filtros por Categoría
        </Typography>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Autocomplete
              size="small"
              options={Object.entries(statusConfig).map(([value, { label }]) => ({ value, label }))}
              getOptionLabel={(option) => option.label || ''}
              value={Object.entries(statusConfig).find(([value, { label }]) => value === filters.status)?.[1] ? 
                { value: filters.status, label: Object.entries(statusConfig).find(([value, { label }]) => value === filters.status)[1].label } : null}
              onChange={(event, newValue) => {
                handleFilterChange('status', newValue ? newValue.value : '');
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Estado del Pedido"
                  placeholder="Buscar estado..."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: filters.status ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: filters.status ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
                      },
                      '&.Mui-focused': {
                        backgroundColor: filters.status ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: filters.status ? theme.palette.text.primary : 'inherit',
                      fontWeight: filters.status ? 'bold' : 'normal',
                    },
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) => option.value === value.value}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Autocomplete
              size="small"
              options={providers}
              getOptionLabel={(option) => option.name || ''}
              value={providers.find(p => p.id === filters.providerId) || null}
              onChange={(event, newValue) => {
                handleFilterChange('providerId', newValue ? newValue.id : '');
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Proveedor"
                  placeholder="Buscar proveedor..."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: filters.providerId ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: filters.providerId ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
                      },
                      '&.Mui-focused': {
                        backgroundColor: filters.providerId ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: filters.providerId ? theme.palette.text.primary : 'inherit',
                      fontWeight: filters.providerId ? 'bold' : 'normal',
                    },
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Autocomplete
              size="small"
              options={COMPANIES.map(company => ({ name: company, value: company }))}
              getOptionLabel={(option) => option.name || ''}
              value={COMPANIES.find(c => c === filters.company) ? { name: filters.company, value: filters.company } : null}
              onChange={(event, newValue) => {
                handleFilterChange('company', newValue ? newValue.value : '');
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Empresa"
                  placeholder="Buscar empresa..."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: filters.company ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: filters.company ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
                      },
                      '&.Mui-focused': {
                        backgroundColor: filters.company ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: filters.company ? theme.palette.text.primary : 'inherit',
                      fontWeight: filters.company ? 'bold' : 'normal',
                    },
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) => option.value === value.value}
            />
          </Grid>
        </Grid>

        {/* Filtros de fecha de creación y facturación */}
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Filtros por Fechas
        </Typography>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
              <DatePicker
                label="Creación - Fecha Inicio"
                value={filters.startDate}
                onChange={(date) => handleFilterChange('startDate', date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: filters.startDate ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
                        '&:hover': {
                          backgroundColor: filters.startDate ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
                        },
                        '&.Mui-focused': {
                          backgroundColor: filters.startDate ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: filters.startDate ? theme.palette.text.primary : 'inherit',
                        fontWeight: filters.startDate ? 'bold' : 'normal',
                      },
                    }
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
              <DatePicker
                label="Creación - Fecha Fin"
                value={filters.endDate}
                onChange={(date) => handleFilterChange('endDate', date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: filters.endDate ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
                        '&:hover': {
                          backgroundColor: filters.endDate ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
                        },
                        '&.Mui-focused': {
                          backgroundColor: filters.endDate ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: filters.endDate ? theme.palette.text.primary : 'inherit',
                        fontWeight: filters.endDate ? 'bold' : 'normal',
                      },
                    }
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
              <DatePicker
                label="Facturación - Fecha Inicio"
                value={filters.invoiceStartDate}
                onChange={(date) => handleFilterChange('invoiceStartDate', date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: filters.invoiceStartDate ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
                        '&:hover': {
                          backgroundColor: filters.invoiceStartDate ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
                        },
                        '&.Mui-focused': {
                          backgroundColor: filters.invoiceStartDate ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: filters.invoiceStartDate ? theme.palette.text.primary : 'inherit',
                        fontWeight: filters.invoiceStartDate ? 'bold' : 'normal',
                      },
                    }
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
              <DatePicker
                label="Facturación - Fecha Fin"
                value={filters.invoiceEndDate}
                onChange={(date) => handleFilterChange('invoiceEndDate', date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: filters.invoiceEndDate ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
                        '&:hover': {
                          backgroundColor: filters.invoiceEndDate ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
                        },
                        '&.Mui-focused': {
                          backgroundColor: filters.invoiceEndDate ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: filters.invoiceEndDate ? theme.palette.text.primary : 'inherit',
                        fontWeight: filters.invoiceEndDate ? 'bold' : 'normal',
                      },
                    }
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>

        {/* Botón limpiar filtros */}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12}>
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => setFilters({
                status: '',
                providerId: '',
                company: '',
                startDate: null,
                endDate: null,
                invoiceStartDate: null,
                invoiceEndDate: null
              })}
            >
              Limpiar Todos los Filtros
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Barra de herramientas para acciones masivas */}
      {selectedOrders.length > 0 && (
        <Paper sx={{ mb: 2 }}>
          <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 }, py: 3 }}>
            <Typography
              sx={{ flex: '1 1 100%' }}
              color="inherit"
              variant="subtitle1"
              component="div"
            >
              {selectedOrders.length} pedido(s) seleccionado(s)
              {selectAllPages && ` (de todas las páginas)`}
            </Typography>
            
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckIcon />}
              onClick={handleBulkApprove}
              disabled={isSubmitting || isExporting}
              sx={{ mr: 1, ml: 1 }}
            >
              Aprobar Seleccionados
            </Button>
            
            <Button
              variant="contained"
              color="error"
              startIcon={<ClearIcon />}
              onClick={handleBulkCancel}
              disabled={isSubmitting || isExporting}
              sx={{ mr: 1, ml: 1 }}
            >
              Cancelar Seleccionados
            </Button>

            <Button
              variant="outlined"
              startIcon={<TableViewIcon />}
              onClick={handleExportExcel}
              disabled={isExporting}
              sx={{ mr: 1, ml: 1 }}
            >
              Exportar Excel
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<ArchiveIcon />}
              onClick={handleDownloadInvoices}
              disabled={isExporting}
              sx={{ mr: 1, ml: 1 }}
            >
              Descargar Facturas
            </Button>
          </Toolbar>
        </Paper>
      )}
      
      {/* Tabla de pedidos */}
      <Paper sx={{ width: '100%', mb: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedOrders.length > 0 && selectedOrders.length < allOrderIds.length}
                        checked={allOrderIds.length > 0 && selectedOrders.length === allOrderIds.length}
                        onChange={handleSelectAllClick}
                        inputProps={{ 'aria-label': 'Seleccionar todas las órdenes' }}
                      />
                    </TableCell>
                    <TableCell>ID</TableCell>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Proveedor</TableCell>
                    <TableCell>Empresa</TableCell>
                    <TableCell>Base Imponible</TableCell>
                    <TableCell>Total</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Fecha Facturación</TableCell>
                    <TableCell>Método de Pago</TableCell>
                    <TableCell>Fecha de Pago</TableCell>
                    <TableCell>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No se encontraron pedidos
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    orders.map((order) => {
                      const isItemSelected = isOrderSelected(order.id);
                      
                      return (
                        <TableRow 
                          key={order.id}
                          selected={isItemSelected}
                          onClick={() => handleOpenOrderModal(order)}
                          sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                        >
                          <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={isItemSelected}
                              inputProps={{ 'aria-labelledby': `enhanced-table-checkbox-${order.id}` }}
                              onChange={() => handleSelectOrder(order.id)}
                            />
                          </TableCell>
                          <TableCell>{order.id}</TableCell>
                        <TableCell>
                          {order.creationDate && !isNaN(new Date(order.creationDate).getTime()) 
                            ? format(new Date(order.creationDate), 'dd/MM/yyyy', { locale: es })
                            : 'N/A'
                          }
                        </TableCell>
                        <TableCell>{order.provider?.name || 'N/A'}</TableCell>
                        <TableCell>{order.company || 'N/A'}</TableCell>
                        <TableCell>
                          {order.baseAmount !== null && order.baseAmount !== undefined && !isNaN(order.baseAmount) 
                            ? `${Number(order.baseAmount).toFixed(2)} €` 
                            : (order.orderItems && order.orderItems.length > 0
                              ? `${order.orderItems.reduce((sum, item) => sum + (Number(item.basePrice) || 0), 0).toFixed(2)} €`
                              : 'N/A')
                          }
                        </TableCell>
                        <TableCell>
                          {order.amount !== null && order.amount !== undefined && !isNaN(order.amount) 
                            ? `${Number(order.amount).toFixed(2)} €` 
                            : 'N/A'
                          }
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={statusConfig[order.status]?.label || order.status}
                            color={statusConfig[order.status]?.color || 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {order.invoiceDate && !isNaN(new Date(order.invoiceDate).getTime()) 
                            ? format(new Date(order.invoiceDate), 'dd/MM/yyyy', { locale: es })
                            : 'N/A'
                          }
                        </TableCell>
                        <TableCell>{order.paymentMethod || 'N/A'}</TableCell>
                        <TableCell>
                          {order.paymentDate && !isNaN(new Date(order.paymentDate).getTime()) 
                            ? format(new Date(order.paymentDate), 'dd/MM/yyyy', { locale: es })
                            : 'N/A'
                          }
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenPreviewModal(order);
                            }}
                            title="Previsualizar factura"
                            sx={{ mr: 1 }}
                          >
                            <SearchIcon />
                          </IconButton>
                          {order.status !== 'COMPLETED' && order.status !== 'CANCELLED' && (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteOrder(order);
                              }}
                              title="Cancelar pedido"
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Filas por página"
              labelDisplayedRows={({ from, to, count }) => 
                `${from}-${to} de ${count}`
              }
            />
          </>
        )}
      </Paper>

      {/* Modal de pedido */}
      <Dialog
        open={openOrderModal}
        onClose={handleCloseOrderModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {selectedOrder ? 'Editar Pedido' : 'Nuevo Pedido'}
            <IconButton onClick={handleCloseOrderModal}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!selectedOrder ? (
            // Para nuevos pedidos: mostrar drag & drop
            <Box>
              <Typography variant="h6" gutterBottom>
                Subir Factura
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Arrastra y suelta una factura en PDF o ZIP para crear un nuevo pedido.
              </Typography>
              <FileDropZone
                onOrderCreated={handleOrderCreated}
                disabled={isSubmitting}
              />
            </Box>
          ) : (
            // Para pedidos existentes: mostrar stepper y formulario
            <Box>
              <OrderStepper
                currentStatus={selectedOrder.status}
                orderData={selectedOrder}
                onStepClick={(step) => {
                  // TODO: Implementar cambio de estado
                  console.log('Cambiar a estado:', step);
                }}
              />

              <Box sx={{ mt: 2 }}>
                <OrderForm
                  status={selectedOrder.status}
                  orderData={selectedOrder}
                  providers={providers}
                  onSubmit={handleSubmitOrder}
                  onCancel={handleCloseOrderModal}
                  isSubmitting={isSubmitting}
                />
              </Box>

              {selectedOrder.files && selectedOrder.files.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Archivos del Pedido
                  </Typography>
                  <OrderViewer
                    files={selectedOrder.files}
                    onFileUpload={selectedOrder.status === 'PENDING_PAYMENT' ? handleFileUpload : null}
                    onFileDelete={(file) => {
                      // No permitir eliminar archivos en estados finalizados
                      if (selectedOrder.status === 'COMPLETED' || selectedOrder.status === 'CANCELLED') {
                        showSnackbar('No se pueden eliminar archivos de un pedido finalizado', 'error');
                        return;
                      }
                      // Solo permitir eliminar archivos adicionales, no el original
                      if (file.isOriginal) {
                        showSnackbar('No se puede eliminar el archivo original del pedido', 'error');
                        return;
                      }
                      handleFileDelete(file);
                    }}
                    onFileDownload={handleFileDownload}
                    readOnly={selectedOrder.status === 'COMPLETED' || selectedOrder.status === 'CANCELLED' || selectedOrder.status !== 'PENDING_PAYMENT'}
                    allowUpload={selectedOrder.status === 'PENDING_PAYMENT'}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de cancelación */}
      <Dialog
        open={openCancelModal}
        onClose={handleCancelDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Cancelar Pedido
            <IconButton onClick={handleCancelDialogClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {selectedOrder?.id === 'bulk' 
              ? `¿Está seguro de que desea cancelar ${selectedOrder.orders.length} pedido(s) seleccionado(s)?`
              : `¿Está seguro de que desea cancelar el pedido #${selectedOrder?.id}?`
            }
          </Typography>
          
          {selectedOrder?.id === 'bulk' && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, maxHeight: 200, overflow: 'auto' }}>
              <Typography variant="subtitle2" gutterBottom>
                Pedidos a cancelar:
              </Typography>
              {selectedOrder.orders.map((order, index) => (
                <Typography key={order.id} variant="body2" sx={{ mb: 0.5 }}>
                  • Pedido #{order.id} - {order.provider?.name || 'Sin proveedor'} - {order.company || 'Sin empresa'}
                </Typography>
              ))}
            </Box>
          )}
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Por favor, proporcione un motivo para la cancelación y un email para enviar la notificación.
          </Typography>

          <TextField
            label="Motivo de cancelación"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            fullWidth
            multiline
            rows={4}
            required
            error={!cancelReason.trim()}
            helperText={!cancelReason.trim() ? 'El motivo es obligatorio' : ''}
            sx={{ mb: 2 }}
          />

          <TextField
            label="Email de notificación"
            type="email"
            value={notificationEmail}
            onChange={(e) => setNotificationEmail(e.target.value)}
            fullWidth
            required
            error={!notificationEmail.trim()}
            helperText={!notificationEmail.trim() ? 'El email es obligatorio' : 'Se enviará una notificación de cancelación a este email'}
            placeholder="ejemplo@correo.com"
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCancelDialogClose}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmCancel}
            disabled={!cancelReason.trim() || !notificationEmail.trim()}
          >
            Continuar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de confirmación final */}
      <Dialog
        open={openConfirmCancelModal}
        onClose={() => setOpenConfirmCancelModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Confirmar Cancelación de Pedido
            <IconButton onClick={() => setOpenConfirmCancelModal(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            {selectedOrder?.id === 'bulk' 
              ? `Está a punto de cancelar permanentemente ${selectedOrder.orders.length} pedido(s). Esta acción no se puede deshacer.`
              : 'Está a punto de cancelar permanentemente este pedido. Esta acción no se puede deshacer.'
            }
          </Alert>

          <Typography variant="h6" gutterBottom>
            Resumen de la cancelación:
          </Typography>
          
          {selectedOrder?.id === 'bulk' ? (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, maxHeight: 300, overflow: 'auto' }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Pedidos a cancelar:</strong> {selectedOrder.orders.length}
              </Typography>
              
              {selectedOrder.orders.map((order, index) => (
                <Box key={order.id} sx={{ mb: 2, p: 1, bgcolor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Pedido ID:</strong> #{order.id}
                  </Typography>
                  
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Proveedor:</strong> {order.provider?.name || order.providerName || 'Sin asignar'}
                  </Typography>
                  
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Empresa:</strong> {order.company || 'Sin asignar'}
                  </Typography>
                  
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Importe:</strong> {
                      order.amount && order.amount > 0 
                        ? `${Number(order.amount).toFixed(2)} €` 
                        : 'Sin definir'
                    }
                  </Typography>
                  
                  <Typography variant="body2">
                    <strong>Estado actual:</strong> {statusConfig[order.status]?.label || order.status}
                  </Typography>
                </Box>
              ))}
              
              <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
                <strong>Email de notificación:</strong> {notificationEmail}
              </Typography>
              
              <Typography variant="body2">
                <strong>Motivo:</strong> {cancelReason}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Pedido ID:</strong> #{selectedOrder?.id}
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Proveedor:</strong> {selectedOrder?.provider?.name || selectedOrder?.providerName || 'Sin asignar'}
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Empresa:</strong> {selectedOrder?.company || 'Sin asignar'}
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Importe:</strong> {
                  selectedOrder?.amount && selectedOrder.amount > 0 
                    ? `${Number(selectedOrder.amount).toFixed(2)} €` 
                    : 'Sin definir'
                }
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Estado actual:</strong> {statusConfig[selectedOrder?.status]?.label || selectedOrder?.status}
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Email de notificación:</strong> {notificationEmail}
              </Typography>
              
              <Typography variant="body2">
                <strong>Motivo:</strong> {cancelReason}
              </Typography>
            </Box>
          )}

          <Typography variant="body2" color="text.secondary">
            Se enviará un email de notificación a <strong>{notificationEmail}</strong> informando sobre la cancelación {selectedOrder?.id === 'bulk' ? 'de los pedidos' : 'del pedido'}.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button 
            onClick={() => setOpenConfirmCancelModal(false)}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleFinalConfirmCancel}
            disabled={isSubmitting}
            startIcon={isSubmitting && <CircularProgress size={20} />}
          >
            {isSubmitting ? 'Cancelando...' : 'Confirmar Cancelación'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de previsualización de factura */}
      <Dialog
        open={openPreviewModal}
        onClose={handleClosePreviewModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Previsualización - Pedido #{selectedOrder?.id}
            <IconButton onClick={handleClosePreviewModal}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {selectedOrder && selectedOrder.files && (
            <OrderViewer
              files={selectedOrder.files}
              onFileDownload={handleFileDownload}
              readOnly={true}
            />
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClosePreviewModal}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Orders;